"use client";

import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const STEPS = [
  {
    number: "01",
    title: "Choose Your Format",
    description:
      "Pick from five creative formats — Short Story, Anime, Drama, Netflix-style Season, or Poem. Each has its own dedicated builder.",
    chips: ["Story", "Anime", "Drama", "Season", "Poem"],
    gradient: "from-blue-400/20 to-blue-600/15",
    glow: "rgba(99,102,241,0.15)",
    image:
      "https://i.pinimg.com/736x/87/a4/93/87a493ac48912d7a99a85c120635d2cd.jpg",
    imageAlt: "Library with diverse books and genres",
  },
  {
    number: "02",
    title: "Build Your World",
    description:
      "Set your genre, write your premise, and design characters in the Character Studio. Guided steps eliminate the blank page.",
    chips: ["10+ genres", "Character Studio", "Plot arcs"],
    gradient: "from-purple-400/20 to-violet-500/15",
    glow: "rgba(139,92,246,0.15)",
    image:
      "https://i.pinimg.com/736x/00/7e/11/007e119cb3831ec424db219a6ac8d3d5.jpg",
    imageAlt: "Creative writer building story world with notes",
  },
  {
    number: "03",
    title: "Generate in Seconds",
    description:
      "Hit Generate and your complete, polished story appears in under a minute — read it or listen with AI voice narration.",
    chips: ["Under 60 sec", "Full prose", "Audio narration"],
    gradient: "from-orange-400/20 to-yellow-400/15",
    glow: "rgba(251,146,60,0.15)",
    image:
      "https://i.pinimg.com/736x/04/41/6b/04416ba1e784ce696eec3967fa6adf61.jpg",
    imageAlt: "AI generating story content instantly",
  },
  {
    number: "04",
    title: "Publish & Win",
    description:
      "Share publicly, enter storytelling contests, earn awards and certificates, and build a real audience for your work.",
    chips: ["Community", "Contests & prizes", "Awards"],
    gradient: "from-green-400/20 to-emerald-500/15",
    glow: "rgba(52,211,153,0.15)",
    image:
      "https://i.pinimg.com/736x/b6/e5/53/b6e553d4d25d251a81a20ec834697730.jpg",
    imageAlt: "Celebration confetti for publishing and winning",
  },
];

const SPRING = { type: "spring", damping: 30, mass: 1, stiffness: 160 } as const;

export default function StepsSection() {
  const [active, setActive] = useState(0);

  return (
    <section
      className="relative overflow-hidden bg-stone-50 py-20 md:py-32"
      id="how-it-works"
    >
      <div className="absolute top-0 left-0 right-0 h-28 bg-gradient-to-b from-yellow-100 to-transparent pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <motion.div
          className="mb-16 text-center md:mb-24"
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.75, ease: "easeOut" }}
        >
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="w-8 h-px bg-gray-400" />
            <span className="text-[10px] font-semibold uppercase tracking-[0.25em] text-gray-500">
              How It Works
            </span>
            <div className="w-8 h-px bg-gray-400" />
          </div>
          <h2 className="mb-5 leading-tight">
            <span className="block text-4xl font-black text-gray-900 sm:text-5xl md:text-6xl">
              From idea to story
            </span>
            <span className="block text-4xl font-black sm:text-5xl md:text-6xl bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              in four steps.
            </span>
          </h2>
          <p className="mx-auto max-w-md text-base font-light text-gray-500 sm:text-lg">
            No writing experience needed. Storly guides every decision — then
            does the heavy lifting for you.
          </p>
        </motion.div>

        {/* ── DESKTOP: Expanding accordion ── */}
        <motion.div
          className="hidden md:flex gap-3 h-[440px] mx-auto max-w-6xl"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.7, delay: 0.2, ease: "easeOut" }}
        >
          {STEPS.map((step, i) => {
            const isActive = active === i;

            return (
              <motion.div
                key={step.number}
                onClick={() => setActive(i)}
                animate={{
                  width: isActive ? "55%" : "15%",
                  boxShadow: isActive
                    ? `0 24px 52px ${step.glow}`
                    : "0 4px 16px rgba(0,0,0,0.12)",
                }}
                transition={SPRING}
                className="relative overflow-hidden rounded-2xl cursor-pointer flex-shrink-0"
                role="button"
                tabIndex={0}
                aria-expanded={isActive}
                onKeyDown={(e) => e.key === "Enter" && setActive(i)}
              >
                {/* Background image */}
                <Image
                  src={step.image}
                  alt={step.imageAlt}
                  fill
                  unoptimized
                  className="object-cover object-center transition-transform duration-700"
                  style={{ transform: isActive ? "scale(1.03)" : "scale(1.08)" }}
                />

                {/* Gradient overlay */}
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${step.gradient}`}
                />

                {/* Shimmer sweep on activation */}
                <AnimatePresence>
                  {isActive && (
                    <motion.div
                      key="shimmer"
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent pointer-events-none z-10"
                      initial={{ x: "-100%" }}
                      animate={{ x: "100%" }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.6, ease: "easeInOut" }}
                    />
                  )}
                </AnimatePresence>

                {/* ── Content: collapsed / expanded ── */}
                <AnimatePresence mode="wait" initial={false}>
                  {isActive ? (
                    <motion.div
                      key="expanded"
                      className="absolute inset-0 flex flex-col justify-between z-20"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.22, delay: 0.1 }}
                    >
                      {/* Step badge — top right */}
                      <div className="flex justify-end p-4">
                        <span className="rounded-full bg-black/30 backdrop-blur-md px-2.5 py-1 text-[10px] font-bold text-white uppercase tracking-wider border border-white/20">
                          Step {i + 1}
                        </span>
                      </div>

                      {/* Glass text panel — bottom */}
                      <div className="m-3 rounded-xl bg-black/40 backdrop-blur-md border border-white/15 p-5">
                        <h3 className="mb-2 text-xl font-black leading-tight text-white sm:text-2xl">
                          {step.title}
                        </h3>
                        <p className="mb-4 text-xs leading-relaxed text-white/80">
                          {step.description}
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {step.chips.map((chip, ci) => (
                            <motion.span
                              key={chip}
                              className="rounded-full bg-white/15 backdrop-blur-sm border border-white/20 px-2.5 py-1 text-[10px] font-bold text-white"
                              initial={{ opacity: 0, y: 5 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.18 + ci * 0.06, duration: 0.24 }}
                            >
                              {chip}
                            </motion.span>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="collapsed"
                      className="absolute inset-0 flex flex-col items-center justify-center gap-4 z-20 bg-black/30"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.18 }}
                    >
                      <span className="text-5xl font-black text-white">
                        {step.number}
                      </span>
                      <span className="text-[14px] font-black text-white/90 uppercase tracking-[0.18em] [writing-mode:vertical-lr]">
                        {step.title}
                      </span>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Dot indicators — desktop */}
        <div className="hidden md:flex justify-center gap-2 mt-5">
          {STEPS.map((_, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                active === i ? "w-6 bg-gray-700" : "w-1.5 bg-gray-300 hover:bg-gray-400"
              }`}
              aria-label={`Go to step ${i + 1}`}
            />
          ))}
        </div>

        {/* ── MOBILE: Vertical stacked cards ── */}
        <div className="md:hidden grid gap-4">
          {STEPS.map((step, i) => (
            <motion.div
              key={step.number}
              className="relative overflow-hidden rounded-2xl min-h-52"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-20px" }}
              transition={{ duration: 0.6, delay: i * 0.1, ease: "easeOut" }}
            >
              <Image
                src={step.image}
                alt={step.imageAlt}
                fill
                unoptimized
                className="object-cover object-center"
              />
              <div className={`absolute inset-0 bg-gradient-to-br ${step.gradient}`} />

              <div className="relative z-20 p-4 flex flex-col justify-between h-full">
                <div className="flex justify-end">
                  <span className="rounded-full bg-black/30 backdrop-blur-md border border-white/20 px-2.5 py-1 text-[10px] font-bold text-white uppercase tracking-wider">
                    Step {i + 1}
                  </span>
                </div>
                <div className="mt-4 rounded-xl bg-black/40 backdrop-blur-md border border-white/15 p-4">
                  <h3 className="mb-2 text-xl font-black leading-tight text-white">
                    {step.title}
                  </h3>
                  <p className="mb-3 text-xs leading-relaxed text-white/80">
                    {step.description}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {step.chips.map((chip) => (
                      <span
                        key={chip}
                        className="rounded-full bg-white/15 backdrop-blur-sm border border-white/20 px-2.5 py-1 text-[10px] font-bold text-white"
                      >
                        {chip}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          className="mt-14 md:mt-16 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <Link href="https://app.storly.world/register" target="_blank" rel="noreferrer">
            <motion.span
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 px-8 py-4 text-sm font-bold text-white shadow-lg cursor-pointer"
              whileHover={{ scale: 1.04, boxShadow: "0 12px 32px rgba(99,102,241,0.4)" }}
              whileTap={{ scale: 0.97 }}
              transition={{ duration: 0.2 }}
            >
              <Sparkles className="h-4 w-4" />
              Start Free — No Credit Card
            </motion.span>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
